import { AsyncCheckoutOrderType } from "@/components/Checkout/types";

import { styled } from "@mui/material/styles";
import React, { useState } from "react";
import { useCheckoutUI } from "../../../CheckoutUIProvider";
import {
  brandclub_colors,
  Button,
  formatMoney,
  getFeaturedRetailerImage,
  getRetailerFromId,
} from "@brandclub/common-ui";
import { useCheckoutCartInfo } from "../../../hooks/useCheckoutCartInfo";

import {
  saveRetailerCredentials,
  updateOrderType,
} from "@/api/rest/checkoutApi";
import EditConnectPopup from "../retailerConnect/EditConnectPopup";
import { useCheckoutRetailer } from "../../../hooks/useCheckoutRetailer";

const CheckoutOptionsContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: 18,
}));

const getUserAccountDisplayText = ({
  rewardAmount,
  connectRewardAmount,
  estimatedGuestOrderTotal,
  estimatedUserAccountOrderTotal,
  estimatedUserAccountOrderShippingFees,
}: {
  rewardAmount: number;
  connectRewardAmount: number;
  estimatedGuestOrderTotal: number;
  estimatedUserAccountOrderTotal: number;
  estimatedUserAccountOrderShippingFees: number;
}) => {
  let line1Text = "";
  let line1Highlight = "";
  let line2Text = "";
  let line2Highlight = "";
  if (rewardAmount) {
    line1Text = `Earn ${formatMoney(rewardAmount)} in cash rewards`;
  } else {
    line1Text = `Earn cash rewards and member-only exclusives`;
  }

  if (connectRewardAmount) {
    line1Highlight = `get ${formatMoney(connectRewardAmount)} in bonus rewards`;
  }

  if (!estimatedUserAccountOrderShippingFees) {
    line2Text = `Get fast, free shipping`;
  } else {
    line2Text = `Pay ${formatMoney(
      estimatedUserAccountOrderShippingFees
    )} in estimated shipping and service fees`;
  }

  if (estimatedGuestOrderTotal > estimatedUserAccountOrderTotal) {
    line2Highlight = `get ${formatMoney(
      estimatedGuestOrderTotal - estimatedUserAccountOrderTotal
    )} in estimated savings`;
  }

  return {
    line1Text,
    line1Highlight,
    line2Text,
    line2Highlight,
  };
};

const getGuestDisplayText = ({
  rewardAmount,
  connectRewardAmount,
  estimatedGuestOrderTotal,
  estimatedUserAccountOrderTotal,
  estimatedGuestOrderShippingFees,
  estimatedGuestServiceFees,
}: {
  rewardAmount: number;
  connectRewardAmount: number;
  estimatedGuestOrderTotal: number;
  estimatedUserAccountOrderTotal: number;
  estimatedGuestOrderShippingFees: number;
  estimatedGuestServiceFees: number;
}) => {
  let line1Text = "";
  let line1Highlight = "";
  let line2Text = "";
  let line2Highlight = "";

  if (rewardAmount) {
    line1Text = `Earn ${formatMoney(
      rewardAmount - (connectRewardAmount ?? 0)
    )} in cash rewards`;
  } else {
    line1Text = "";
  }

  if (estimatedGuestOrderTotal > estimatedUserAccountOrderTotal) {
    line2Text = `Pay ${formatMoney(
      estimatedGuestOrderShippingFees + estimatedGuestServiceFees
    )} in estimated shipping and service fees`;
  }

  return {
    line1Text,
    line1Highlight,
    line2Text,
    line2Highlight,
  };
};

const OptionContainer = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  border: `1px solid ${brandclub_colors.lightGray}`,
  borderRadius: 8,
  overflow: "hidden",

  ".mobile": {
    display: "none",
    [theme.breakpoints.down("md")]: {
      display: "flex",
    },
  },
  ".desktop": {
    display: "flex",
    [theme.breakpoints.down("md")]: {
      display: "none",
    },
  },

  ".option-head": {
    display: "flex",
    justifyContent: "flex-start",
    flexDirection: "row",
    alignItems: "center",
    gap: 8,

    height: 72,
    padding: "5px 20px",
    backgroundColor: brandclub_colors.blueWhite,
    borderBottom: `1px solid ${brandclub_colors.lightGray}`,
    [theme.breakpoints.down("md")]: {
      height: 40,
    },

    ".option-head-checkbox": {
      width: 20,
      cursor: "pointer",
      height: 20,
      borderRadius: 999,
      border: `1px solid ${brandclub_colors.lightGray}`,
      backgroundColor: brandclub_colors.white,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      [theme.breakpoints.down("md")]: {
        width: 16,
        height: 16,
      },

      " .option-head-checkbox-selected": {
        backgroundColor: theme.vars.palette.primary.main,
        width: 10,
        height: 10,
        borderRadius: 999,
        [theme.breakpoints.down("md")]: {
          width: 8,
          height: 8,
        },
      },
    },
    ".option-head-icon": {
      width: 32,
      height: 32,
      borderRadius: 999,
      objectFit: "cover",
      [theme.breakpoints.down("md")]: {
        width: 20,
        height: 20,
      },
    },
    ".option-head-title": {
      fontSize: 20,
      flex: 1,
      fontWeight: 600,
      [theme.breakpoints.down("md")]: {
        fontSize: 14,
      },
    },
  },
  ".option-body": {
    display: "flex",
    flexDirection: "column",
    gap: 8,
    padding: "17px 50px 17px 50px",
    fontSize: 14,
    [theme.breakpoints.down("md")]: {
      padding: "12px 20px 16px 20px",
      fontSize: 12,
    },

    ".option-body-line1": {
      padding: "0px 0px",
      [theme.breakpoints.down("md")]: {
        padding: "0px 15px",
      },
    },
    ".option-body-line2": {
      padding: "0px 0px",
      [theme.breakpoints.down("md")]: {
        padding: "0px 15px",
      },
    },
  },
}));

const Option = ({
  selected = false,
  onClick,
  type,
}: {
  selected: boolean;
  onClick: () => void;
  type: AsyncCheckoutOrderType;
}) => {
  const { currentRetailer } = useCheckoutRetailer();

  const [state, send] = useCheckoutUI();
  const { rewardAmounts } = useCheckoutCartInfo();
  const { checkoutIntent } = state.context;

  const title =
    type === AsyncCheckoutOrderType.USER_ACCOUNT
      ? `Checkout with ${
          currentRetailer?.retailerId
            ? getRetailerFromId(currentRetailer?.retailerId)
            : "Retailer"
        }`
      : "Checkout as a guest";

  const { line1Text, line1Highlight, line2Text, line2Highlight } =
    type === AsyncCheckoutOrderType.USER_ACCOUNT
      ? getUserAccountDisplayText({
          rewardAmount: rewardAmounts.totalRewardAmount,
          connectRewardAmount: rewardAmounts.retailerConnectRewardAmount,
          estimatedGuestOrderTotal:
            checkoutIntent?.estimatedGuestOrderSummary?.total ?? 0,
          estimatedUserAccountOrderTotal:
            checkoutIntent?.estimatedUserAccountOrderSummary?.total ?? 0,
          estimatedUserAccountOrderShippingFees:
            checkoutIntent?.estimatedUserAccountOrderSummary?.shippingFees ?? 0,
        })
      : getGuestDisplayText({
          rewardAmount: rewardAmounts.totalRewardAmount,
          connectRewardAmount: rewardAmounts.retailerConnectRewardAmount,
          estimatedGuestOrderTotal:
            checkoutIntent?.estimatedGuestOrderSummary?.total ?? 0,
          estimatedUserAccountOrderTotal:
            checkoutIntent?.estimatedUserAccountOrderSummary?.total ?? 0,
          estimatedGuestOrderShippingFees:
            checkoutIntent?.estimatedGuestOrderSummary?.shippingFees ?? 0,
          estimatedGuestServiceFees:
            checkoutIntent?.estimatedGuestOrderSummary?.serviceFee ?? 0,
        });

  const onSaveCredentials = async (username: string, password: string) => {
    try {
      const checkoutIntent = await saveRetailerCredentials({
        retailerId: currentRetailer?.retailerId?.toString() ?? "",
        checkoutIntentId: state.context.checkoutIntent?.checkoutIntentId!,
        credentials: {
          username,
          password,
        },
      });

      send({
        type: "SET_CHECKOUT_INTENT",
        data: { checkoutIntent },
      });
    } catch (error) {
      console.error(error);
    }
  };

  const openEdit = () => {
    EditConnectPopup.show({
      connectReward: rewardAmounts?.retailerConnectRewardAmount,
      onSaveCredentials: onSaveCredentials,
      currentUsername: "",
      currentPassword: "",
      retailer: currentRetailer,

      checkoutIntentUsername: checkoutIntent?.retailerConnection?.username,
    });
  };

  return (
    <OptionContainer>
      <div className="option-head">
        <div className="option-head-checkbox" onClick={onClick} role="button">
          {selected && <div className="option-head-checkbox-selected"></div>}
        </div>
        {type === AsyncCheckoutOrderType.USER_ACCOUNT &&
          currentRetailer?.retailerId && (
            <img
              className="option-head-icon"
              src={getFeaturedRetailerImage(currentRetailer?.retailerId)}
              alt="retailer-icon"
            />
          )}
        <div className="option-head-title">{title}</div>
        {type === AsyncCheckoutOrderType.USER_ACCOUNT && (
          <div className="option-head-button desktop">
            <Button
              variant={"solid"}
              sx={{
                paddingLeft: "25px",
                paddingRight: "25px",
              }}
              onClick={openEdit}
            >
              {rewardAmounts.retailerConnectRewardAmount
                ? `Connect to get ${formatMoney(
                    rewardAmounts.retailerConnectRewardAmount
                  )} in perks`
                : "Connect to get exclusive perks"}
            </Button>
          </div>
        )}
      </div>
      <div className="option-body">
        <div className="option-body-line1">
          {line1Text}
          {line1Highlight ? (
            <span className="option-body-line1-highlight">
              {" "}
              - <strong>{line1Highlight}</strong>
            </span>
          ) : null}
        </div>
        <div className="option-body-line2">
          {line2Text}
          {line2Highlight ? (
            <span className="option-body-line1-highlight">
              {" "}
              - <strong>{line2Highlight}</strong>
            </span>
          ) : null}
        </div>
        {type === AsyncCheckoutOrderType.USER_ACCOUNT && (
          <div className="option-head-button mobile">
            <Button
              variant={"solid"}
              disableMaxWidth
              sx={(theme) => ({
                paddingLeft: "15px",
                paddingRight: "15px",
                width: "100%",
              })}
              onClick={openEdit}
            >
              {rewardAmounts.retailerConnectRewardAmount
                ? `Connect to get ${formatMoney(
                    rewardAmounts.retailerConnectRewardAmount
                  )} in perks`
                : "Connect to get exclusive perks"}
            </Button>
          </div>
        )}
      </div>
    </OptionContainer>
  );
};

const CheckoutOptions = () => {
  const [machineState, send] = useCheckoutUI();

  const toggleOrderType = async (orderType: AsyncCheckoutOrderType) => {
    const { checkoutIntent } = await updateOrderType({
      orderType,
      checkoutIntentId: machineState.context.checkoutIntent?.checkoutIntentId!,
    });
    console.log("checkoutIntent", checkoutIntent);
    send({
      type: "SET_CHECKOUT_INTENT",
      data: { checkoutIntent },
    });
  };

  // @ts-ignore
  const { checkoutIntent, userRetailerCheckoutOrder } = machineState.context;
  console.log(checkoutIntent, userRetailerCheckoutOrder);

  const orderType =
    checkoutIntent?.orderType ?? AsyncCheckoutOrderType.USER_ACCOUNT;

  const handleOrderTypeChange = (orderType: AsyncCheckoutOrderType) => {
    console.log("orderType", orderType);
    toggleOrderType(orderType);
  };

  return (
    <CheckoutOptionsContainer>
      <Option
        selected={orderType === AsyncCheckoutOrderType.USER_ACCOUNT}
        onClick={() =>
          handleOrderTypeChange(AsyncCheckoutOrderType.USER_ACCOUNT)
        }
        type={AsyncCheckoutOrderType.USER_ACCOUNT}
      />
      <Option
        selected={orderType === AsyncCheckoutOrderType.GUEST}
        onClick={() => handleOrderTypeChange(AsyncCheckoutOrderType.GUEST)}
        type={AsyncCheckoutOrderType.GUEST}
      />
    </CheckoutOptionsContainer>
  );
};

export default CheckoutOptions;
